/**
 * Storefront v6 — the business behind the food: reviews (only the merchant's
 * own), the story, the information grid (hours, address, ordering facts),
 * the photo gallery and the FAQ.
 */
import React, { useState } from 'react';
import { ChevronDown, MapPin, Clock, Tag, ExternalLink } from 'lucide-react';
import { Photo, SectionHead, money } from '../primitives';
import { sizesFor } from '../data/media';
import { hoursRows } from './Shell';

const Stars = ({ n = 5 }) => (
  <span aria-label={`${n} out of 5 stars`} style={{ color: 'var(--s6-primary)' }}>{'★'.repeat(Math.max(0, Math.min(5, Number(n) || 0)))}</span>
);

/** Only merchant-typed testimonials; nothing is rendered when there are none. */
export const Reviews = ({ testimonials = [], copy = {}, theme }) => {
  const list = testimonials.filter((t) => t && t.quote);
  if (!list.length) return null;
  const [lead, ...rest] = list;
  return (
    <section id="reviews" className="s6-section s6-surface-tint" data-testid="s6-reviews">
      <div className="s6-container">
        <SectionHead eyebrow={copy.eyebrow} title={copy.title} emphasis={copy.emphasis} accentStyle={theme.accentStyle} />
        <div className="grid gap-8 md:grid-cols-2 items-start">
          <blockquote className="md:pr-10 md:border-r" style={{ borderColor: 'var(--s6-line)' }}>
            <span aria-hidden="true" className="block text-6xl leading-none font-bold" style={{ color: 'var(--s6-primary)', fontFamily: 'var(--s6-heading)' }}>“</span>
            <p className="s6-h2 !text-[clamp(1.6rem,3.2vw,2.6rem)]">{lead.quote}</p>
            <div className="mt-4"><Stars n={lead.rating} /></div>
            {lead.name && <footer className="mt-2 font-semibold">{lead.name}</footer>}
          </blockquote>
          <div className="grid gap-4">
            {rest.slice(0, 3).map((t, i) => (
              <div key={i} className="s6-card p-5">
                <p className="text-[15px] leading-relaxed">“{t.quote}”</p>
                <div className="mt-3 flex items-center justify-between text-sm"><Stars n={t.rating} />{t.name && <span className="font-semibold">{t.name}</span>}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export const About = ({ brand, copy = {}, theme }) => {
  const html = brand.about_body_html || brand.story_body_html || brand.about_html || '';
  const photo = brand.story_image_url || null;
  if (!html && !photo) return null;
  return (
    <section id="about" className="s6-section s6-surface-white" data-testid="s6-about">
      <div className="s6-container grid gap-10 md:grid-cols-2 items-center">
        {photo && <Photo src={photo} alt="" ratio="4 / 3" sizes={sizesFor('tile')} />}
        <div>
          {copy.eyebrow && <span className="s6-eyebrow">{copy.eyebrow}</span>}
          <h2 className="s6-h2">{copy.title}{copy.emphasis ? ' ' : ''}{copy.emphasis && <span className={theme.accentStyle === 'script' ? 's6-script s6-emphasis' : 's6-emphasis'}>{copy.emphasis}</span>}</h2>
          {html && <div className="s6-prose mt-5" dangerouslySetInnerHTML={{ __html: html }} data-testid="s6-about-body" />}
        </div>
      </div>
    </section>
  );
};

const TODAY = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'][new Date().getDay()];

/** Hours (today highlighted), where to find us, ordering facts. Cells render only from real data. */
export const InfoGrid = ({ brand, settings, currency }) => {
  const rows = settings?.store_hours_enabled ? hoursRows(settings?.operating_hours) : [];
  const address = brand.contact_address || settings?.pickup_address || null;
  const mapHref = brand.google_maps_url || (address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}` : null);
  const minOrder = Number(settings?.minimum_order_value) > 0 ? money(settings.minimum_order_value, currency) : null;
  const prep = Number(settings?.order_prep_time_minutes) > 0 ? `${settings.order_prep_time_minutes} min` : null;
  const ordering = [minOrder && `Minimum order ${minOrder}`, prep && `Ready in about ${prep}`, settings?.pickup_address && 'Pickup available'].filter(Boolean);
  const cells = [
    rows.length > 0 && { key: 'hours', title: 'Hours', Icon: Clock, body: (
      <ul className="space-y-1 text-sm tabular-nums">
        {rows.map((r) => <li key={r.key} className={`flex justify-between gap-4 ${r.key === TODAY ? 'font-bold' : ''}`} style={r.key === TODAY ? { color: 'var(--s6-primary)' } : undefined}><span>{r.label}</span><span>{r.text}</span></li>)}
      </ul>
    ) },
    address && { key: 'find', title: 'Find us', Icon: MapPin, body: (
      <div className="text-sm space-y-2">
        <p>{address}</p>
        {mapHref && <a href={mapHref} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-semibold underline underline-offset-4" style={{ color: 'var(--s6-primary)' }}>Open in Maps <ExternalLink className="w-3.5 h-3.5" /></a>}
        {brand.contact_phone && <p><a href={`tel:${brand.contact_phone}`}>{brand.contact_phone}</a></p>}
      </div>
    ) },
    ordering.length > 0 && { key: 'ordering', title: 'Ordering', Icon: Tag, body: <ul className="space-y-1 text-sm">{ordering.map((o) => <li key={o}>{o}</li>)}</ul> },
  ].filter(Boolean);
  if (!cells.length) return null;
  return (
    <section id="info" className="s6-section !pt-0" data-testid="s6-info">
      <div className="s6-container">
        <div className={`grid border rounded-[var(--s6-radius-card)] overflow-hidden ${cells.length >= 3 ? 'md:grid-cols-3' : 'md:grid-cols-2'}`} style={{ borderColor: 'var(--s6-line)' }}>
          {cells.map((c, i) => (
            <div key={c.key} className={`p-6 md:p-8 ${i ? 'border-t md:border-t-0 md:border-l' : ''}`} style={{ borderColor: 'var(--s6-line)' }} data-testid={`s6-info-${c.key}`}>
              <h3 className="s6-h3 mb-3 flex items-center gap-2"><c.Icon className="w-4 h-4" style={{ color: 'var(--s6-primary)' }} aria-hidden="true" />{c.title}</h3>
              {c.body}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/** Gapless bento: as many cells as photos, the first one larger. */
export const Gallery = ({ items = [], copy = {}, theme, onOpen }) => {
  const photos = items.filter((g) => g && g.image_url).slice(0, 8);
  if (!photos.length) return null;
  return (
    <section id="gallery" className="s6-section" data-testid="s6-gallery">
      <div className="s6-container">
        {(copy.title) && <SectionHead eyebrow={copy.eyebrow} title={copy.title} emphasis={copy.emphasis} accentStyle={theme.accentStyle} />}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 [grid-auto-rows:minmax(140px,auto)]">
          {photos.map((g, i) => (
            <button key={i} type="button" onClick={() => onOpen(i)} className={`s6-photo relative ${i === 0 ? 'col-span-2 row-span-2' : ''}`} style={{ aspectRatio: i === 0 ? '1 / 1' : '1 / 1' }} aria-label={g.caption ? `View photo: ${g.caption}` : `View photo ${i + 1}`} data-testid={`s6-gallery-${i}`}>
              <img src={g.image_url} alt={g.caption || ''} loading="lazy" decoding="async" className="w-full h-full object-cover" />
              {g.generated && <span className="absolute bottom-2 right-2 text-[10px] px-1.5 py-0.5 rounded bg-black/55 text-white" data-testid="storefront-ai-image-tag">AI generated image</span>}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export const Faq = ({ faqs = [], copy = {}, theme }) => {
  const [open, setOpen] = useState(-1);
  const list = faqs.filter((f) => f && f.q);
  if (!list.length) return null;
  return (
    <section id="faq" className="s6-section" data-testid="s6-faq">
      <div className="max-w-3xl mx-auto px-5 sm:px-6">
        <SectionHead eyebrow={copy.eyebrow} title={copy.title} emphasis={copy.emphasis} accentStyle={theme.accentStyle} />
        <div className="s6-rows border-t" style={{ borderColor: 'var(--s6-line)' }}>
          {list.map((f, i) => (
            <div key={i} data-testid={`s6-faq-${i}`}>
              <button type="button" onClick={() => setOpen(open === i ? -1 : i)} aria-expanded={open === i ? 'true' : 'false'} className="w-full flex items-center justify-between gap-4 py-4 text-left s6-h3">
                {f.q}<ChevronDown className={`w-5 h-5 flex-shrink-0 transition-transform ${open === i ? 'rotate-180' : ''}`} aria-hidden="true" />
              </button>
              {open === i && <p className="pb-5 text-[15px] leading-relaxed" style={{ color: 'var(--s6-ink-muted)' }}>{f.a}</p>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
